import { create } from 'zustand';
import type { CriticalAiResult } from '@sr-test-wizard/shared';
import { criticalAiApi } from '../lib/criticalAiApi';

/**
 * State for the AI Critical-File Nominator card (Phase 3). Fed from the
 * `critical-ai-progress` WS stream; on a terminal status the persisted result
 * is re-fetched. Kept separate from runStore so the heavy nomination table does
 * not re-render the rest of the phase.
 */
export interface CriticalAiProgress {
  status: 'started' | 'progress' | 'completed' | 'failed';
  message?: string;
  tool?: string;
}

interface CriticalAiState {
  runId: string | null;
  result: CriticalAiResult | null;
  loading: boolean;
  error: string | null;
  progress: CriticalAiProgress | null;

  load: (runId: string) => Promise<void>;
  nominate: () => Promise<void>;
  applyProgress: (snapshot: CriticalAiProgress) => void;
  refetch: () => Promise<void>;
  clear: () => void;
}

export const useCriticalAiStore = create<CriticalAiState>((set, get) => ({
  runId: null,
  result: null,
  loading: false,
  error: null,
  progress: null,

  async load(runId) {
    const isDifferent = get().runId !== null && get().runId !== runId;
    set({ runId, loading: true, error: null, ...(isDifferent ? { result: null, progress: null } : {}) });
    try {
      const { result } = await criticalAiApi.get(runId);
      set({ result });
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  async nominate() {
    const { runId } = get();
    if (!runId) return;
    set({ progress: { status: 'started' }, error: null });
    try {
      await criticalAiApi.nominate(runId);
    } catch (err) {
      set({ error: (err as Error).message, progress: null });
    }
  },

  applyProgress(snapshot) {
    set({ progress: snapshot });
    if (snapshot.status === 'completed' || snapshot.status === 'failed') {
      void get().refetch();
    }
  },

  async refetch() {
    const { runId } = get();
    if (!runId) return;
    try {
      const { result } = await criticalAiApi.get(runId);
      set({ result });
    } catch {
      // keep existing state; WS already surfaced the error
    }
  },

  clear() {
    set({ runId: null, result: null, loading: false, error: null, progress: null });
  },
}));
